"use client";

import { EmptyState, ErrorState } from "@/components/admin/UIStates";
import BlogCard from "@/components/ui/BlogCard";
import BlogCardHero from "@/components/ui/BlogCardHero";
import BlogCardHeroSkeleton from "@/components/ui/BlogCardHeroSkeleton";
import BlogCardSkeleton from "@/components/ui/BlogCardSkeleton";
import ContentSearchSidebar from "@/components/ui/ContentSearchSidebar";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { ADMIN_LIST_FILTERS_DEBOUNCE_MS } from "@/lib/constants/adminListUi";
import { FileText } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

interface Blog {
    _id: string;
    title: string;
    slug: string;
    excerpt: string;
    featuredImage?: string;
    author: string;
    tags: string[];
    publishDate: string;
    formattedDate: string;
    readTime: number;
    views: number;
}

interface BlogResponse {
    blogs: Blog[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
    };
}

export default function BlogsClient({ initialBlogs }: { initialBlogs: Blog[] }) {
    const BLOGS_PER_PAGE = 6;
    const [blogs, setBlogs] = useState<Blog[]>(initialBlogs || []);
    const [loading, setLoading] = useState(!initialBlogs?.length ? true : false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreBlogs, setHasMoreBlogs] = useState(true);
    const [categories, setCategories] = useState<string[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>("All Categories");
    const [postSearch, setPostSearch] = useState<string>("");
    const [isMd, setIsMd] = useState(false);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const debouncedPostSearch = useDebouncedValue(
        postSearch,
        ADMIN_LIST_FILTERS_DEBOUNCE_MS
    );

    // Fetch categories from API
    const fetchCategories = useCallback(async () => {
        try {
            const response = await fetch('/api/categories', {
                cache: 'no-store', // Always fetch fresh categories
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch categories');
            }

            const data = await response.json();
            if (data.categories && Array.isArray(data.categories)) {
                // Extract category names and sort them
                const categoryNames = data.categories.map((cat: { name: string }) => cat.name).sort();
                setCategories(["All Categories", ...categoryNames]);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
            // Keep existing categories if API fails, or set empty if none exist
            setCategories(prev => prev.length > 1 ? prev : ["All Categories"]);
        }
    }, []);

    const fetchBlogs = useCallback(async (isInitialLoad = false, pageToLoad = 1) => {
        if (isInitialLoad) {
            setLoading(true);
            setBlogs([]); // ✅ clear old blogs before fetching
        } else {
            setLoadingMore(true);
        }
        setError(null);

        try {
            const params = new URLSearchParams({
                page: String(pageToLoad),
                limit: String(BLOGS_PER_PAGE),
            });
            if (selectedCategory !== "All Categories") {
                params.set("category", selectedCategory);
            }
            if (debouncedPostSearch.trim()) {
                params.set('search', debouncedPostSearch.trim());
            }

            const response = await fetch(`/api/blogs?${params.toString()}`, {
                cache: 'no-store' // Always fetch fresh blogs
            });

            if (!response.ok) {
                throw new Error('Failed to fetch blogs');
            }

            const data: BlogResponse = await response.json();

            if (isInitialLoad) {
                setBlogs(data.blogs); // replace old data
                setCurrentPage(1);
            } else {
                setBlogs(prevBlogs => [...prevBlogs, ...data.blogs]);
                setCurrentPage(pageToLoad);
            }

            setHasMoreBlogs(pageToLoad < data.pagination.pages);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch blogs');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [BLOGS_PER_PAGE, debouncedPostSearch, selectedCategory]);

    // Handle window resize to determine if viewport is XL (≥1280px)
    useEffect(() => {
        const handleResize = () => {
            setIsMd(window.innerWidth >= 768);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Fetch categories on mount
    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Refresh categories when window regains focus (e.g., after updating categories in admin)
    useEffect(() => {
        const handleFocus = () => {
            fetchCategories();
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [fetchCategories]);

    // Fetch blogs on mount and when category changes
    // useEffect(() => {
    //     fetchBlogs(true);
    // }, [selectedCategory, fetchBlogs]);

    // useEffect(() => {
    //     if (!initialBlogs?.length) {
    //         fetchBlogs(true);
    //     }
    // }, [selectedCategory, fetchBlogs, initialBlogs]);

    useEffect(() => {
        fetchBlogs(true, 1);
    }, [selectedCategory, fetchBlogs]);

    useEffect(() => {
        const target = loadMoreRef.current;
        if (!target || loading || loadingMore || !hasMoreBlogs) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting && !loadingMore && hasMoreBlogs) {
                    fetchBlogs(false, currentPage + 1);
                }
            },
            {
                root: null,
                rootMargin: isMd ? "320px" : "180px",
                threshold: 0,
            }
        );

        observer.observe(target);
        return () => observer.disconnect();
    }, [currentPage, fetchBlogs, hasMoreBlogs, isMd, loading, loadingMore]);

    // const handleCategoryChange = (category: string) => {
    //     setSelectedCategory(category);
    //     setCurrentPage(1);
    //     setHasMoreBlogs(true);
    // };


    const handleCategoryChange = (category: string) => {
        setSelectedCategory(category);
        setCurrentPage(1);
        setHasMoreBlogs(true);
    };
    const handlePostSearchChange = (value: string) => {
        setPostSearch(value);
        setCurrentPage(1);
        setHasMoreBlogs(true);
    };
    const hasResults = blogs.length > 0;
    const heroItem = hasResults ? blogs[0] : undefined;
    const remaining = hasResults ? blogs.slice(1) : [];
    const bottomCards = remaining;

    if (loading) {
        return (
            <main className="overflow-hidden">
                <section className="lg:py-24 py-16 relative text-white">
                    <div className="custom-container mx-auto">
                        {/* Heading Skeleton */}
                        <div className="lg:mb-20 mb-5 flex justify-between md:items-center flex-col md:flex-row">
                            <div className="animate-pulse">
                                <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
                                <div className="h-12 bg-gray-200 rounded w-96"></div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 xl:gap-6">
                            <div className="xl:col-span-9">
                                {/* Hero card skeleton (updated to match new full-width hero layout) */}
                                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                                    <div className="xl:col-span-12">
                                        <BlogCardHeroSkeleton />
                                    </div>
                                </div>

                                {/* Bottom Grid skeleton (show 6 placeholders to approximate content) */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                    <BlogCardSkeleton />
                                    <BlogCardSkeleton />
                                    <BlogCardSkeleton />
                                    <BlogCardSkeleton />
                                    <BlogCardSkeleton />
                                    <BlogCardSkeleton />
                                </div>
                            </div>

                            {/* Categories Filter Column Skeleton */}
                            <div className="xl:col-span-3 order-1 xl:order-2">
                                <div className="bg-white rounded-lg p-4 xl:p-6 shadow-lg sticky top-4 xl:top-20">
                                    <div className="h-6 bg-gray-200 rounded w-24 mb-4 animate-pulse"></div>

                                    {/* Search Input Skeleton */}
                                    <div className="relative mb-4 xl:mb-6">
                                        <div className="h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                                    </div>

                                    {/* Categories List Skeleton */}
                                    <div className="space-y-0 h-[300px] xl:h-[350px] overflow-y-auto custom-scrollbar">
                                        {[...Array(8)].map((_, index) => (
                                            <div key={index} className="border-b border-gray-100 py-2 xl:py-3 px-2">
                                                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        );
    }

    if (error) {
        return (
            <main>
                <section className="lg:py-24 py-16 relative text-white">
                    <div className="custom-container mx-auto">
                        <div className="max-w-2xl mx-auto">
                            <ErrorState
                                error={{
                                    type: 'server',
                                    message: 'Unable to load blogs',
                                    details: error,
                                    canRetry: true
                                }}
                                onRetry={() => fetchBlogs(true)}
                            />
                        </div>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main>
            <section className="lg:py-24 py-16 relative text-white">
                <div className="custom-container mx-auto">
                    {/* Heading */}
                    <div className="lg:mb-16 mb-5 flex justify-between md:items-center flex-col md:flex-row">

                        <div>

                            <h1 className="tracking-tight leading-tight mb-0 pb-0">
                                <span className="block text-2xl lg:text-4xl font-semibold text-[#1b1b1b]">
                                    Enterprise
                                </span>
                                <span className="block text-3xl sm:text-4xl xl:text-5xl 2xl:text-6xl font-bold text-[#272d57]">
                                    Marketing Intelligence
                                </span>
                            </h1>
                        </div>


                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 xl:gap-6">
                        <div className="xl:col-span-9 order-2 xl:order-1">
                            {/* Top Grid: Hero */}
                            {hasResults ? (
                                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                                    {/* Hero card */}
                                    {heroItem && (
                                        <div className="xl:col-span-12 ">
                                            <BlogCardHero
                                                image={heroItem.featuredImage || '/api/placeholder/400/250'}
                                                date={heroItem.formattedDate}
                                                title={heroItem.title}
                                                excerpt={heroItem.excerpt}
                                                category={(() => {
                                                    const firstTag = heroItem.tags && heroItem.tags.length > 0 ? heroItem.tags[0]?.trim() : '';
                                                    // Only show category if it's a valid, non-generic value
                                                    if (!firstTag || firstTag.toLowerCase() === 'blog' || firstTag.toLowerCase() === 'general' || firstTag.toLowerCase() === 'blog post') {
                                                        return '';
                                                    }
                                                    return firstTag;
                                                })()}
                                                slug={heroItem.slug}
                                            />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="mt-8">
                                    <EmptyState
                                        icon={<FileText className="h-16 w-16 text-gray-400" />}
                                        title="No blogs found"
                                        description={
                                            selectedCategory !== "All Categories"
                                                ? `No blogs found in the "${selectedCategory}" category. Try selecting a different category or clearing your search.`
                                                : "No blogs are available at the moment. Check back later for new content."
                                        }
                                        action={
                                            selectedCategory !== "All Categories" ? (
                                                <button
                                                    onClick={() => handleCategoryChange("All Categories")}
                                                    className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium"
                                                >
                                                    View All Categories
                                                </button>
                                            ) : null
                                        }
                                    />
                                </div>
                            )}

                            {/* Bottom Grid: remaining cards */}
                            {hasResults && bottomCards.length > 0 && (
                                <div className="grid disabling grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                    {bottomCards.map((card) => (
                                        <BlogCard
                                            key={card._id}
                                            image={card.featuredImage || '/api/placeholder/400/250'}
                                            date={card.formattedDate}
                                            category={(() => {
                                                const firstTag = card.tags && card.tags.length > 0 ? card.tags[0]?.trim() : '';
                                                // Only show category if it's a valid, non-generic value
                                                if (!firstTag || firstTag.toLowerCase() === 'blog' || firstTag.toLowerCase() === 'general' || firstTag.toLowerCase() === 'blog post') {
                                                    return '';
                                                }
                                                return firstTag;
                                            })()}
                                            title={card.title}
                                            excerpt={card.excerpt}
                                            slug={card.slug}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Auto-load trigger on scroll */}
                            {hasMoreBlogs && <div ref={loadMoreRef} className="h-1 w-full mt-8" />}

                            {/* Loading More Skeletons */}
                            {loadingMore && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                    <BlogCardSkeleton />
                                    <BlogCardSkeleton />
                                    {isMd && <BlogCardSkeleton />}
                                    {isMd && <BlogCardSkeleton />}
                                </div>
                            )}
                        </div>

                        {/* Categories Filter Column */}
                        <div className="xl:col-span-3 order-1 xl:order-2">
                            <ContentSearchSidebar
                                categories={categories}
                                selectedCategory={selectedCategory}
                                postSearch={postSearch}
                                contentLabel="blogs"
                                postSearchPlaceholder="Search blogs..."
                                onPostSearchChange={handlePostSearchChange}
                                onCategoryChange={handleCategoryChange}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
